import { useState, type KeyboardEvent } from "react";
import type { Authorization, ReportSettings } from "../../api/types";
import { useTill } from "../../app/till-context";
import { t } from "../../i18n";
import { ConfirmDialog } from "../../ui/ConfirmDialog";

interface Props {
  authorization: Authorization;
  settings: ReportSettings;
  onSaved: (settings: ReportSettings) => void;
}

/**
 * Platformen, as in the prototype: the online platforms of the shop in the order of the Z dialog.
 * A removed platform stays in the old Z's and quarterly overviews.
 */
export function PlatformsTab({ authorization, settings, onSaved }: Props) {
  const { api, report, toast } = useTill();
  const [name, setName] = useState("");
  const [removing, setRemoving] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const text = t.reports.platforms;
  const platforms = settings.platforms;

  const save = async (next: string[], message?: string): Promise<boolean> => {
    setBusy(true);
    try {
      const answer = await api.request<{ settings: ReportSettings }>("PUT", "/device/reports/settings", {
        staffToken: authorization.token,
        body: { platforms: next, accountant: settings.accountant },
      });
      onSaved(answer.settings);
      if (message) toast(message);
      return true;
    } catch (error) {
      report(error);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const add = () => {
    const typed = name.trim();
    if (!typed) return;
    if (platforms.some((platform) => platform.toLowerCase() === typed.toLowerCase())) {
      toast(text.exists(typed));
      return;
    }
    void save([...platforms, typed], text.added(typed)).then((saved) => {
      if (saved) setName("");
    });
  };

  const up = (index: number) => {
    const next = [...platforms];
    [next[index - 1], next[index]] = [next[index]!, next[index - 1]!];
    void save(next);
  };

  return (
    <>
      <p className="note rsince">{text.note}</p>
      <div className="platlist">
        {platforms.map((platform, index) => (
          <div className="platrow" key={platform}>
            <b>{platform}</b>
            <button
              type="button"
              className="btn small"
              disabled={index === 0 || busy}
              aria-label={text.upOf(platform)}
              title={text.up}
              onClick={() => {
                up(index);
              }}
            >
              ↑
            </button>
            <button
              type="button"
              className="btn small danger"
              disabled={busy}
              aria-label={text.removeOf(platform)}
              onClick={() => {
                setRemoving(platform);
              }}
            >
              {text.remove}
            </button>
          </div>
        ))}
        {platforms.length === 0 && <p className="note">{text.none}</p>}
      </div>
      <div className="rowacts padd">
        <input
          className="pnew"
          value={name}
          maxLength={60}
          placeholder={text.placeholder}
          aria-label={text.newLabel}
          onChange={(event) => {
            setName(event.target.value);
          }}
          onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
            if (event.key === "Enter") {
              event.preventDefault();
              add();
            }
          }}
        />
        <button type="button" className="btn primary" disabled={busy} onClick={add}>
          {text.add}
        </button>
      </div>
      <p className="note">{text.footer}</p>
      {removing !== null && (
        <ConfirmDialog
          message={text.removeConfirm(removing)}
          confirm={text.remove}
          onConfirm={() => {
            void save(
              platforms.filter((platform) => platform !== removing),
              text.removed(removing),
            );
          }}
          onClose={() => {
            setRemoving(null);
          }}
        />
      )}
    </>
  );
}
